import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import { toast } from "react-toastify";

import {
  useNavigate,
  useParams,
} from "react-router-dom";

import ProjectApi
  from "../../api/projectApi";  

import TestCaseApi
  from "../../api/TestCaseApi";

import BugApi
  from "../../api/BugApi";


function AssignedTestingDetails() {

  /* =========================================
     ROUTER
  ========================================= */

  const { id } = useParams();

  const navigate =
    useNavigate();


  /* =========================================
     STATE
  ========================================= */

  const [
    project,
    setProject,
  ] = useState(null);

  const [
    testCases,
    setTestCases,
  ] = useState([]);

  const [
    bugs,
    setBugs,
  ] = useState([]);

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    actionLoading,
    setActionLoading,
  ] = useState(false);

  const [
    dialog,
    setDialog,
  ] = useState({
    type: null,
    data: null,
  });

  const [
    dialogInput,
    setDialogInput,
  ] = useState("");

  const [
    error,
    setError,
  ] = useState("");


  /* =========================================
     BUG MODAL STATE
  ========================================= */

  const [
    selectedTestCase,
    setSelectedTestCase,
  ] = useState(null);

  const [
    showBugForm,
    setShowBugForm,
  ] = useState(false);

  const [
    bugForm,
    setBugForm,
  ] = useState({
    title: "",
    description: "",
    stepsToReproduce: [""],
    expectedResult: "",
    actualResult: "",
    severity: "medium",
    priority: "medium",
  });


  /* =========================================
     RESPONSE HELPERS
  ========================================= */

  const getProjectFromResponse =
    (response) => {

      if (!response) {
        return null;
      }

      if (
        response?.data?.project
      ) {
        return response.data.project;
      }

      if (
        response?.data?.data?.project
      ) {
        return response.data.data.project;
      }

      if (
        response?.data?.data
      ) {
        return response.data.data;
      }

      if (
        response?.data &&
        response.data._id
      ) {
        return response.data;
      }

      return null;
    };


  const getArrayFromResponse =
    (
      response,
      possibleKeys = [],
    ) => {

      if (!response) {
        return [];
      }

      const responseData =
        response?.data;

      if (
        Array.isArray(
          responseData,
        )
      ) {
        return responseData;
      }

      for (
        const key of possibleKeys
      ) {

        if (
          Array.isArray(
            responseData?.[key],
          )
        ) {
          return responseData[key];
        }

        if (
          Array.isArray(
            responseData?.data?.[key],
          )
        ) {
          return responseData.data[key];
        }

      }

      if (
        Array.isArray(
          responseData?.data,
        )
      ) {
        return responseData.data;
      }

      return [];
    };


  /* =========================================
     LOAD ALL PROJECT DATA
  ========================================= */

  const loadData =
    async () => {

      if (!id) {

        setError(
          "Project ID is missing.",
        );

        setLoading(false);

        return;
      }

      try {

        setLoading(true);

        setError("");

        const results =
          await Promise.allSettled([

            ProjectApi.details(id),

            TestCaseApi
              .getProjectTestCases(
                id,
              ),

            BugApi
              .getProjectBugs(
                id,
              ),

          ]);


        /* =====================================
           PROJECT
        ===================================== */

        const projectResult =
          results[0];

        if (
          projectResult.status ===
          "fulfilled"
        ) {

          const projectResponse =
            projectResult.value;

          console.log(
            "PROJECT DETAILS RESPONSE:",
            projectResponse,
          );

          const projectData =
            getProjectFromResponse(
              projectResponse,
            );

          if (
            projectData
          ) {

            setProject(
              projectData,
            );

          } else {

            setProject(null);

            setError(
              "Project details could not be loaded.",
            );

          }

        } else {

          console.error(
            "PROJECT LOAD ERROR:",
            projectResult.reason,
          );

          setProject(null);

          setError(
            "Unable to load project details.",
          );

        }


        /* =====================================
           TEST CASES
        ===================================== */

        const testCaseResult =
          results[1];

        if (
          testCaseResult.status ===
          "fulfilled"
        ) {

          console.log(
            "TEST CASE RESPONSE:",
            testCaseResult.value,
          );

          const testCaseData =
            getArrayFromResponse(
              testCaseResult.value,
              [
                "testCases",
                "projects",
                "items",
              ],
            );

          setTestCases(
            testCaseData,
          );

        } else {

          console.error(
            "TEST CASE LOAD ERROR:",
            testCaseResult.reason,
          );

          setTestCases([]);

        }


        /* =====================================
           BUGS
        ===================================== */

        const bugResult =
          results[2];

        if (
          bugResult.status ===
          "fulfilled"
        ) {

          console.log(
            "BUG RESPONSE:",
            bugResult.value,
          );

          const bugData =
            getArrayFromResponse(
              bugResult.value,
              [
                "bugs",
                "items",
              ],
            );

          setBugs(
            bugData,
          );

        } else {

          console.error(
            "BUG LOAD ERROR:",
            bugResult.reason,
          );

          setBugs([]);

        }

      } catch (
        error
      ) {

        console.error(
          "LOAD TESTING DATA ERROR:",
          error,
        );

        setError(
          "Something went wrong while loading project data.",
        );

      } finally {

        setLoading(false);

      }

    };


  /* =========================================
     INITIAL LOAD
  ========================================= */

  useEffect(() => {

    loadData();

  }, [id]);


  /* =========================================
     START TESTING
  ========================================= */

  const handleStartTesting =
    async () => {

      try {

        setActionLoading(true);

        const res =
          await ProjectApi
            .startTesting(id);

        console.log(
          "START TESTING RESPONSE:",
          res,
        );

        if (
          res?.code === 200 ||
          res?.status === 200
        ) {

          toast.success(
            "Testing started successfully."
          );

          await loadData();

        } else {

          toast.error(
            res?.message ||
              "Unable to start testing."
          );

        }

      } catch (
        error
      ) {

        console.error(
          "START TESTING ERROR:",
          error,
        );

        toast.error(
          error?.response?.data?.message ||
            error?.message ||
            "Unable to start testing."
        );

      } finally {

        setActionLoading(false);

      }

    };


  /* =========================================
     PASS TEST CASE
  ========================================= */

  const handlePass = (testCase) => {

    setDialog({
      type: "passTestCase",
      data: testCase,
    });

    setDialogInput(
      testCase.actualResult || ""
    );

  };


  const confirmPassTestCase =
    async () => {

      const testCase =
        dialog.data;

      if (!testCase) {
        return;
      }

      if (!dialogInput.trim()) {

        toast.warning(
          "Actual result is required."
        );

        return;
      }

      try {

        setActionLoading(true);

        const res =
          await TestCaseApi.updateStatus(
            testCase._id,
            {
              status: "passed",
              actualResult:
                dialogInput.trim(),
            }
          );

        console.log(
          "PASS TEST CASE RESPONSE:",
          res
        );

        if (
          res?.code === 200 ||
          res?.status === 200 ||
          res?.data?.code === 200
        ) {

          toast.success(
            "Test case marked as passed."
          );

          setDialog({
            type: null,
            data: null,
          });

          setDialogInput("");

          await loadData();

        } else {

          toast.error(
            res?.message ||
              "Unable to mark test case as passed."
          );

        }

      } catch (error) {

        console.error(
          "PASS TEST ERROR:",
          error
        );

        toast.error(
          error?.response?.data?.message ||
            error?.message ||
            "Unable to mark test case as passed."
        );

      } finally {

        setActionLoading(false);

      }

    };


  /* =========================================
     BLOCK TEST CASE
  ========================================= */

  const handleBlocked = (
    testCase
  ) => {

    setDialog({
      type: "blockTestCase",
      data: testCase,
    });

    setDialogInput(
      testCase.actualResult || ""
    );

  };


  const confirmBlockTestCase =
    async () => {

      const testCase =
        dialog.data;

      if (!testCase) {
        return;
      }

      if (!dialogInput.trim()) {

        toast.warning(
          "Please enter a blocking reason."
        );

        return;
      }

      try {

        setActionLoading(true);

        const res =
          await TestCaseApi.updateStatus(
            testCase._id,
            {
              status: "blocked",
              actualResult:
                dialogInput.trim(),
            }
          );

        if (
          res?.code === 200 ||
          res?.status === 200
        ) {

          toast.success(
            "Test case marked as blocked."
          );

          setDialog({
            type: null,
            data: null,
          });

          setDialogInput("");

          await loadData();

        } else {

          toast.error(
            res?.message ||
              "Unable to block test case."
          );

        }

      } catch (error) {

        console.error(
          "BLOCK TEST CASE ERROR:",
          error
        );

        toast.error(
          error?.response?.data?.message ||
            error?.message ||
            "Unable to block test case."
        );

      } finally {

        setActionLoading(false);

      }

    };


  /* =========================================
     OPEN BUG FORM
  ========================================= */

  const handleFail = (
    testCase
  ) => {

    setSelectedTestCase(
      testCase
    );

    setBugForm({

      title:
        `Issue in ${testCase.title}`,

      description: "",

      stepsToReproduce:
        testCase.steps?.length
          ? [...testCase.steps]
          : [""],

      expectedResult:
        testCase.expectedResult || "",

      actualResult:
        testCase.actualResult || "",

      severity: "medium",

      priority: "medium",

    });

    setShowBugForm(true);

  };


  /* =========================================
     CREATE BUG
  ========================================= */

  const handleCreateBug =
    async (e) => {

      e.preventDefault();

      if (!selectedTestCase) {

        toast.warning(
          "Please select a test case."
        );

        return;
      }

      if (!bugForm.title.trim()) {

        toast.warning(
          "Bug title is required."
        );

        return;
      }

      if (!bugForm.actualResult.trim()) {

        toast.warning(
          "Actual result is required."
        );

        return;
      }

      try {

        setActionLoading(true);

        const payload = {

          projectId: id,

          // Existing failed testcase
          // is linked here.
          testCaseId:
            selectedTestCase._id,

          title:
            bugForm.title.trim(),

          description:
            bugForm.description?.trim() || "",

          stepsToReproduce:
            bugForm.stepsToReproduce
              .map(
                (step) =>
                  step.trim()
              )
              .filter(Boolean),

          expectedResult:
            bugForm.expectedResult?.trim() || "",

          actualResult:
            bugForm.actualResult.trim(),

          severity:
            bugForm.severity,

          priority:
            bugForm.priority,

        };

        console.log(
          "CREATE BUG PAYLOAD:",
          payload
        );

        const res =
          await BugApi.createBug(
            payload
          );

        console.log(
          "CREATE BUG RESPONSE:",
          res
        );

        if (
          res?.code === 201 ||
          res?.status === 201 ||
          res?.code === 200 ||
          res?.status === 200
        ) {

          toast.success(
            "Bug created and assigned to developer successfully."
          );

          setShowBugForm(false);

          setSelectedTestCase(null);

          setBugForm({

            title: "",

            description: "",

            stepsToReproduce:
              [""],

            expectedResult: "",

            actualResult: "",

            severity: "medium",

            priority: "medium",

          });

          await loadData();

        } else {

          toast.error(
            res?.message ||
              "Unable to create bug."
          );

        }

      } catch (error) {

        console.error(
          "CREATE BUG ERROR:",
          error
        );

        toast.error(
          error?.response?.data?.message ||
            error?.message ||
            "Unable to create bug."
        );

      } finally {

        setActionLoading(false);

      }

    };


  /* =========================================
     CLOSE BUG AFTER RETEST
  ========================================= */

  const handleCloseBug = (
    bug
  ) => {

    setDialog({
      type: "closeBug",
      data: bug,
    });

  };


  const confirmCloseBug =
    async () => {

      const bug =
        dialog.data;

      if (!bug) {
        return;
      }

      try {

        setActionLoading(true);

        const res =
          await BugApi.closeBug(
            bug._id
          );

        if (
          res?.code === 200 ||
          res?.status === 200
        ) {

          const testCaseId =
            bug.testCaseId?._id ||
            bug.testCaseId;

          if (testCaseId) {

            await TestCaseApi.updateStatus(
              testCaseId,
              {
                status: "passed",

                actualResult:
                  "Bug fix verified successfully.",
              }
            );

          }

          toast.success(
            "Bug retested successfully and closed."
          );

          setDialog({
            type: null,
            data: null,
          });

          setDialogInput("");

          await loadData();

        } else {

          toast.error(
            res?.message ||
              "Unable to close bug."
          );

        }

      } catch (error) {

        console.error(
          "CLOSE BUG ERROR:",
          error
        );

        toast.error(
          error?.response?.data?.message ||
            error?.message ||
            "Unable to close bug."
        );

      } finally {

        setActionLoading(false);

      }

    };


  /* =========================================
     REOPEN BUG
  ========================================= */

  const handleReopenBug = (
    bug
  ) => {

    setDialog({
      type: "reopenBug",
      data: bug,
    });

    setDialogInput("");

  };


  const confirmReopenBug =
    async () => {

      const bug =
        dialog.data;

      if (!bug) {
        return;
      }

      if (!dialogInput.trim()) {

        toast.warning(
          "Please enter the retest failure reason."
        );

        return;
      }

      try {

        setActionLoading(true);

        const res =
          await BugApi.reopenBug(
            bug._id
          );

        if (
          res?.code === 200 ||
          res?.status === 200
        ) {

          const testCaseId =
            bug.testCaseId?._id ||
            bug.testCaseId;

          if (testCaseId) {

            await TestCaseApi.updateStatus(
              testCaseId,
              {
                status: "failed",

                actualResult:
                  dialogInput.trim(),
              }
            );

          }

          toast.success(
            "Bug reopened and sent back to the developer."
          );

          setDialog({
            type: null,
            data: null,
          });

          setDialogInput("");

          await loadData();

        } else {

          toast.error(
            res?.message ||
              "Unable to reopen bug."
          );

        }

      } catch (error) {

        console.error(
          "REOPEN BUG ERROR:",
          error
        );

        toast.error(
          error?.response?.data?.message ||
            error?.message ||
            "Unable to reopen bug."
        );

      } finally {

        setActionLoading(false);

      }

    };


  /* =========================================
     APPROVE PROJECT
  ========================================= */

  const handleApprove = () => {

    setDialog({
      type: "approveProject",
      data: null,
    });

  };


  const confirmApproveProject =
    async () => {

      try {

        setActionLoading(true);

        const res =
          await ProjectApi.approveTesting(
            id
          );

        if (
          res?.code === 200 ||
          res?.status === 200
        ) {

          toast.success(
            "Project approved successfully."
          );

          setDialog({
            type: null,
            data: null,
          });

          setDialogInput("");

          await loadData();

        } else {

          toast.error(
            res?.message ||
              "Project cannot be approved."
          );

        }

      } catch (error) {

        console.error(
          "APPROVE PROJECT ERROR:",
          error
        );

        toast.error(
          error?.response?.data?.message ||
            error?.message ||
            "Project cannot be approved."
        );

      } finally {

        setActionLoading(false);

      }

    };


  /* =========================================
     BUG FORM STEP HANDLERS
  ========================================= */

  const addStep =
    () => {

      setBugForm(
        (
          previous
        ) => ({

          ...previous,

          stepsToReproduce: [

            ...previous.stepsToReproduce,

            "",

          ],

        })
      );

    };


  const updateStep =
    (
      index,
      value
    ) => {

      setBugForm(
        (
          previous
        ) => {

          const steps = [
            ...previous.stepsToReproduce,
          ];

          steps[index] =
            value;

          return {

            ...previous,

            stepsToReproduce:
              steps,

          };

        }
      );

    };


  const removeStep =
    (
      index
    ) => {

      setBugForm(
        (
          previous
        ) => {

          const updatedSteps =
            previous
              .stepsToReproduce
              .filter(
                (
                  _,
                  stepIndex
                ) =>
                  stepIndex !==
                  index
              );

          return {

            ...previous,

            stepsToReproduce:
              updatedSteps.length
                ? updatedSteps
                : [""],

          };

        }
      );

    };


  /* =========================================
     COMPUTED VALUES
  ========================================= */

  const activeBugs =
    useMemo(
      () =>
        bugs.filter(
          (
            bug
          ) =>
            ![
              "closed",
            ].includes(
              bug.status,
            ),
        ),
      [bugs],
    );


  const passedTests =
    useMemo(
      () =>
        testCases.filter(
          (
            testCase
          ) =>
            testCase.status ===
            "passed",
        ).length,
      [testCases],
    );


  const failedTests =
    useMemo(
      () =>
        testCases.filter(
          (
            testCase
          ) =>
            testCase.status ===
            "failed",
        ).length,
      [testCases],
    );


  const pendingTests =
    useMemo(
      () =>
        testCases.filter(
          (
            testCase
          ) =>
            testCase.status ===
            "pending",
        ).length,
      [testCases],
    );


  const allTestsPassed =

    testCases.length > 0 &&

    testCases.every(
      (
        testCase
      ) =>
        testCase.status ===
        "passed",
    );


  const testingStarted =

    [
      "in_testing",
      "bug_found",
      "retesting",
      "approved",
    ].includes(
      project?.testingStatus,
    );


  /* =========================================
     STATUS BADGES
  ========================================= */

  const getTestStatusClass =
    (
      status
    ) => {

      const statusMap = {

        pending:
          "bg-secondary",

        passed:
          "bg-success",

        failed:
          "bg-danger",

        blocked:
          "bg-warning text-dark",

      };

      return (
        statusMap[status] ||
        "bg-secondary"
      );

    };


  const getBugStatusClass =
    (
      status
    ) => {

      const statusMap = {

        open:
          "bg-danger",

        in_progress:
          "bg-primary",

        fixed:
          "bg-warning text-dark",

        reopened:
          "bg-danger",

        closed:
          "bg-success",

      };

      return (
        statusMap[status] ||
        "bg-secondary"
      );

    };


  /* =========================================
     LOADING
  ========================================= */

  if (
    loading
  ) {

    return (

      <div className="content">

        <div className="card border-0 shadow-sm">

          <div className="card-body text-center py-5">

            Loading testing project...

          </div>

        </div>

      </div>

    );

  }


  /* =========================================
     ERROR / PROJECT NOT FOUND
  ========================================= */

  if (
    !project
  ) {

    return (

      <div className="content">

        <div className="card border-0 shadow-sm">

          <div className="card-body text-center py-5">

            <h5>
              Project Not Found
            </h5>

            <p className="text-muted">

              {
                error ||
                "Unable to load this project."
              }

            </p>

            <button
              className="btn btn-primary"
              onClick={() =>
                navigate(
                  "/assigned-testing",
                )
              }
            >

              ← Back to Assigned Testing

            </button>

          </div>

        </div>

      </div>

    );

  }


  /* =========================================
     MAIN UI
  ========================================= */

  return (

    <div className="content">


      {/* =====================================
         HEADER
      ===================================== */}

      <div className="d-flex justify-content-between align-items-start mb-4">

        <div>

          <button
            type="button"
            className="btn btn-light border mb-3"
            onClick={() =>
              navigate(
                "/assigned-testing",
              )
            }
          >

            ← Back

          </button>


          <h2 className="fw-bold mb-1">

            {
              project.projectName ||
              "Untitled Project"
            }

          </h2>


          <p className="text-muted mb-0">

            Testing Project Details

          </p>

        </div>


        {
          project.testingStatus ===
          "assigned" && (

            <button
              type="button"
              className="btn btn-primary"
              disabled={
                actionLoading
              }
              onClick={
                handleStartTesting
              }
            >

              {
                actionLoading
                  ? "Starting..."
                  : "▶ Start Testing"
              }

            </button>

          )
        }


      </div>


      {/* =====================================
         PROJECT INFO
      ===================================== */}

      <div className="card border-0 shadow-sm mb-4">

        <div className="card-header bg-white">

          <h5 className="mb-0">

            Project Information

          </h5>

        </div>


        <div className="card-body">

          <div className="row">


            <div className="col-md-4 mb-3">

              <small className="text-muted">

                Project Name

              </small>

              <div className="fw-bold">

                {
                  project.projectName ||
                  "-"
                }

              </div>

            </div>


            <div className="col-md-4 mb-3">

              <small className="text-muted">

                Project Status

              </small>

              <div>

                <span className="badge bg-info">

                  {
                    project.projectStatus ||
                    "-"
                  }

                </span>

              </div>

            </div>


            <div className="col-md-4 mb-3">

              <small className="text-muted">

                Testing Status

              </small>

              <div>

                <span className="badge bg-primary">

                  {
                    project.testingStatus ||
                    "-"
                  }

                </span>

              </div>

            </div>


            <div className="col-md-4">

              <small className="text-muted">

                Assigned Developer

              </small>

              <div className="fw-bold">

                {
                  project
                    .assignedDeveloper
                    ?.name ||
                  "-"
                }

              </div>

            </div>


            <div className="col-md-4">

              <small className="text-muted">

                Priority

              </small>

              <div>

                <span className="badge bg-warning text-dark">

                  {
                    project.priority ||
                    "-"
                  }

                </span>

              </div>

            </div>


            <div className="col-md-4">

              <small className="text-muted">

                Deadline

              </small>

              <div>

                {
                  project.deadline
                    ? new Date(
                        project.deadline,
                      ).toLocaleDateString()
                    : "-"
                }

              </div>

            </div>


          </div>

        </div>

      </div>


      {/* =====================================
         TESTING CREDENTIALS
      ===================================== */}

      <div className="card border-0 shadow-sm mb-4">

        <div className="card-header bg-white">

          <h5 className="mb-0">

            Testing Credentials

          </h5>

        </div>


        <div className="card-body">

          <div className="row">


            <div className="col-md-4 mb-3">

              <small className="text-muted">

                Testing URL

              </small>

              <div>

                {
                  project.testingUrl
                    ? (

                      <a
                        href={
                          project.testingUrl
                        }
                        target="_blank"
                        rel="noreferrer"
                      >

                        Open Testing URL

                      </a>

                    )
                    : "-"

                }

              </div>

            </div>


            <div className="col-md-4 mb-3">

              <small className="text-muted">

                Username

              </small>

              <div>

                {
                  project.testingUsername ||
                  "-"
                }

              </div>

            </div>


            <div className="col-md-4 mb-3">

              <small className="text-muted">

                Password

              </small>

              <div>

                {
                  project.testingPassword ||
                  "-"
                }

              </div>

            </div>


            {
              project.testingNotes && (

                <div className="col-12">

                  <small className="text-muted">

                    Testing Notes

                  </small>

                  <div>

                    {
                      project.testingNotes
                    }

                  </div>

                </div>

              )
            }


          </div>

        </div>

      </div>


      {/* =====================================
         TEST SUMMARY
      ===================================== */}

      <div className="row mb-4">


        <div className="col-md-3 mb-3">

          <div className="card border-0 shadow-sm h-100">

            <div className="card-body">

              <small className="text-muted">

                Total Test Cases

              </small>

              <h3 className="mb-0">

                {
                  testCases.length
                }

              </h3>

            </div>

          </div>

        </div>


        <div className="col-md-3 mb-3">

          <div className="card border-0 shadow-sm h-100">

            <div className="card-body">

              <small className="text-muted">

                Passed

              </small>

              <h3 className="text-success mb-0">

                {
                  passedTests
                }

              </h3>

            </div>

          </div>

        </div>


        <div className="col-md-3 mb-3">

          <div className="card border-0 shadow-sm h-100">

            <div className="card-body">

              <small className="text-muted">

                Failed / Pending

              </small>

              <h3 className="text-warning mb-0">

                {
                  failedTests +
                  pendingTests
                }

              </h3>

            </div>

          </div>

        </div>


        <div className="col-md-3 mb-3">

          <div className="card border-0 shadow-sm h-100">

            <div className="card-body">

              <small className="text-muted">

                Active Bugs

              </small>

              <h3 className="text-danger mb-0">

                {
                  activeBugs.length
                }

              </h3>

            </div>

          </div>

        </div>


      </div>


      {/* =====================================
         TEST CASE EXECUTION
      ===================================== */}

      <div className="card border-0 shadow-sm mb-4">

        <div className="card-header bg-white d-flex justify-content-between align-items-center">

          <h5 className="mb-0">

            Test Case Execution

          </h5>

          <span className="badge bg-primary">

            {
              testCases.length
            } Test Cases

          </span>

        </div>


        <div className="card-body">


          {
            !testingStarted && (

              <div className="alert alert-warning">

                Click <strong>Start Testing</strong> before executing test cases.

              </div>

            )
          }


          {
            testCases.length === 0 ? (

              <div className="text-center py-4 text-muted">

                No test cases available for this project.

              </div>

            ) : (

              testCases.map(
                (
                  testCase,
                  index,
                ) => (

                  <div
                    key={
                      testCase._id
                    }
                    className="border rounded p-3 mb-3"
                  >


                    <div className="d-flex justify-content-between align-items-start gap-3">

                      <div>

                        <h6 className="fw-bold mb-2">

                          {index + 1}.{" "}

                          {
                            testCase.title
                          }

                        </h6>


                        {
                          testCase.description && (

                            <p className="text-muted mb-2">

                              {
                                testCase.description
                              }

                            </p>

                          )
                        }

                      </div>


                      <span
                        className={
                          `badge ${getTestStatusClass(
                            testCase.status,
                          )}`
                        }
                      >

                        {
                          testCase.status
                        }

                      </span>

                    </div>


                    {
                      testCase.steps?.length > 0 && (

                        <div className="mb-3">

                          <strong>

                            Steps:

                          </strong>

                          <ol className="mb-0">

                            {
                              testCase.steps.map(
                                (
                                  step,
                                  stepIndex,
                                ) => (

                                  <li
                                    key={
                                      stepIndex
                                    }
                                  >

                                    {
                                      step
                                    }

                                  </li>

                                ),
                              )
                            }

                          </ol>

                        </div>

                      )
                    }


                    <div className="mb-3">

                      <strong>

                        Expected Result:

                      </strong>

                      <div>

                        {
                          testCase.expectedResult ||
                          "-"
                        }

                      </div>

                    </div>


                    {
                      testCase.actualResult && (

                        <div className="mb-3">

                          <strong>

                            Actual Result:

                          </strong>

                          <div>

                            {
                              testCase.actualResult
                            }

                          </div>

                        </div>

                      )
                    }


                    {
                      project.testingStatus !==
                      "approved" && (

                        <div className="d-flex gap-2 flex-wrap">

                          <button
                            type="button"
                            className="btn btn-success btn-sm"
                            disabled={
                              actionLoading ||
                              !testingStarted ||
                              !!testCase.bugId
                            }
                            onClick={() =>
                              handlePass(
                                testCase,
                              )
                            }
                          >

                            ✓ Pass

                          </button>


                          <button
                            type="button"
                            className="btn btn-danger btn-sm"
                            disabled={
                              actionLoading ||
                              !testingStarted ||
                              !!testCase.bugId
                            }
                            onClick={() =>
                              handleFail(
                                testCase,
                              )
                            }
                          >

                            🐞 Fail & Report Bug

                          </button>


                          <button
                            type="button"
                            className="btn btn-warning btn-sm"
                            disabled={
                              actionLoading ||
                              !testingStarted
                            }
                            onClick={() =>
                              handleBlocked(
                                testCase,
                              )
                            }
                          >

                            Blocked

                          </button>

                        </div>

                      )
                    }


                  </div>

                ),
              )

            )
          }


        </div>

      </div>


      {/* =====================================
         PROJECT BUGS
      ===================================== */}

      <div className="card border-0 shadow-sm mb-4">

        <div className="card-header bg-white d-flex justify-content-between align-items-center">

          <h5 className="mb-0">

            Project Bugs

          </h5>

          <span className="badge bg-danger">

            {
              bugs.length
            } Bugs

          </span>

        </div>


        <div className="card-body p-0">

          <div className="table-responsive">

            <table className="table align-middle mb-0">

              <thead className="table-light">

                <tr>

                  <th className="ps-4">

                    Bug

                  </th>

                  <th>

                    Test Case

                  </th>

                  <th>

                    Severity

                  </th>

                  <th>

                    Status

                  </th>

                  <th>

                    Developer

                  </th>

                  <th>

                    Retest

                  </th>

                </tr>

              </thead>


              <tbody>

                {
                  bugs.length > 0 ? (

                    bugs.map(
                      (
                        bug
                      ) => (

                        <tr
                          key={
                            bug._id
                          }
                        >

                          <td className="ps-4">

                            <div className="fw-bold">

                              {
                                bug.title
                              }

                            </div>

                            <small className="text-muted">

                              {
                                bug.actualResult ||
                                bug.description ||
                                "-"
                              }

                            </small>

                          </td>


                          <td>

                            {
                              bug
                                .testCaseId
                                ?.title ||
                              "-"
                            }

                          </td>


                          <td>

                            <span className="badge bg-warning text-dark">

                              {
                                bug.severity
                              }

                            </span>

                          </td>


                          <td>

                            <span
                              className={
                                `badge ${getBugStatusClass(
                                  bug.status,
                                )}`
                              }
                            >

                              {
                                bug.status
                              }

                            </span>

                          </td>


                          <td>

                            {
                              bug
                                .assignedDeveloper
                                ?.name ||

                              project
                                ?.assignedDeveloper
                                ?.name ||

                              "-"
                            }

                          </td>


                          <td>

                            {
                              bug.status ===
                              "fixed" ? (

                                <div className="d-flex gap-2">

                                  <button
                                    type="button"
                                    className="btn btn-success btn-sm"
                                    disabled={
                                      actionLoading
                                    }
                                    onClick={() =>
                                      handleCloseBug(
                                        bug,
                                      )
                                    }
                                  >

                                    ✓ Pass

                                  </button>


                                  <button
                                    type="button"
                                    className="btn btn-danger btn-sm"
                                    disabled={
                                      actionLoading
                                    }
                                    onClick={() =>
                                      handleReopenBug(
                                        bug,
                                      )
                                    }
                                  >

                                    ✕ Fail

                                  </button>

                                </div>

                              ) : (

                                <span className="text-muted">

                                  {
                                    bug.status ===
                                    "closed"

                                      ? "Verified"

                                      : bug.status ===
                                        "in_progress"

                                      ? "Developer Working"

                                      : "Waiting for Developer"
                                  }

                                </span>

                              )}

                          </td>

                        </tr>

                      ),
                    )

                  ) : (

                    <tr>

                      <td
                        colSpan="6"
                        className="text-center py-4 text-muted"
                      >

                        No bugs reported for this project.

                      </td>

                    </tr>

                  )
                }

              </tbody>

            </table>

          </div>

        </div>

      </div>


      {/* =====================================
         FINAL APPROVAL
      ===================================== */}

      <div className="card border-0 shadow-sm">

        <div className="card-body d-flex justify-content-between align-items-center flex-wrap gap-3">

          <div>

            <h5 className="mb-2">

              Final Testing Decision

            </h5>


            {
              allTestsPassed &&
              activeBugs.length === 0 ? (

                <p className="text-success mb-0">

                  ✓ All test cases have passed and no active bugs exist.

                </p>

              ) : (

                <p className="text-muted mb-0">

                  Project can only be approved when all test cases pass and all bugs are closed.

                </p>

              )
            }


          </div>


          <button
            type="button"
            className="btn btn-success"
            disabled={
              actionLoading ||

              !allTestsPassed ||

              activeBugs.length > 0 ||

              project.testingStatus ===
                "approved"
            }
            onClick={
              handleApprove
            }
          >

            {
              project.testingStatus ===
              "approved"

                ? "✓ Project Approved"

                : actionLoading

                  ? "Processing..."

                  : "Approve Project"
            }

          </button>

        </div>

      </div>


      {/* =====================================
         BUG MODAL
      ===================================== */}

      {
        showBugForm &&
        selectedTestCase && (

          <div
            className="modal d-block"
            style={{
              background:
                "rgba(0,0,0,0.5)",
            }}
          >

            <div className="modal-dialog modal-lg">

              <div className="modal-content">


                <div className="modal-header">

                  <h5 className="modal-title">

                    🐞 Report Bug

                  </h5>


                  <button
                    type="button"
                    className="btn-close"
                    onClick={() =>
                      setShowBugForm(
                        false,
                      )
                    }
                  />

                </div>


                <form
                  onSubmit={
                    handleCreateBug
                  }
                >

                  <div className="modal-body">


                    <div className="mb-3">

                      <label className="form-label">

                        Related Test Case

                      </label>

                      <input
                        className="form-control"
                        value={
                          selectedTestCase.title
                        }
                        disabled
                      />

                    </div>


                    <div className="mb-3">

                      <label className="form-label">

                        Bug Title *

                      </label>

                      <input
                        required
                        className="form-control"
                        value={
                          bugForm.title
                        }
                        onChange={(e) =>
                          setBugForm({
                            ...bugForm,

                            title:
                              e.target.value,

                          })
                        }
                      />

                    </div>


                    <div className="mb-3">

                      <label className="form-label">

                        Description

                      </label>

                      <textarea
                        className="form-control"
                        rows="3"
                        value={
                          bugForm.description
                        }
                        onChange={(e) =>
                          setBugForm({
                            ...bugForm,

                            description:
                              e.target.value,

                          })
                        }
                      />

                    </div>


                    <div className="mb-3">

                      <label className="form-label">

                        Steps To Reproduce

                      </label>


                      {
                        bugForm.stepsToReproduce.map(
                          (
                            step,
                            index,
                          ) => (

                            <div
                              key={index}
                              className="d-flex gap-2 mb-2"
                            >

                              <input
                                className="form-control"
                                value={
                                  step
                                }
                                placeholder={
                                  `Step ${index + 1}`
                                }
                                onChange={(e) =>
                                  updateStep(
                                    index,
                                    e.target.value,
                                  )
                                }
                              />


                              <button
                                type="button"
                                className="btn btn-outline-danger"
                                onClick={() =>
                                  removeStep(
                                    index,
                                  )
                                }
                              >

                                ×

                              </button>

                            </div>

                          ),
                        )
                      }


                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm"
                        onClick={
                          addStep
                        }
                      >

                        + Add Step

                      </button>

                    </div>


                    <div className="row">


                      <div className="col-md-6 mb-3">

                        <label className="form-label">

                          Severity *

                        </label>

                        <select
                          className="form-select"
                          value={
                            bugForm.severity
                          }
                          onChange={(e) =>
                            setBugForm({
                              ...bugForm,

                              severity:
                                e.target.value,

                            })
                          }
                        >

                          <option value="low">

                            Low

                          </option>

                          <option value="medium">

                            Medium

                          </option>

                          <option value="high">

                            High

                          </option>

                          <option value="critical">

                            Critical

                          </option>

                        </select>

                      </div>


                      <div className="col-md-6 mb-3">

                        <label className="form-label">

                          Priority

                        </label>

                        <select
                          className="form-select"
                          value={
                            bugForm.priority
                          }
                          onChange={(e) =>
                            setBugForm({
                              ...bugForm,

                              priority:
                                e.target.value,

                            })
                          }
                        >

                          <option value="low">

                            Low

                          </option>

                          <option value="medium">

                            Medium

                          </option>

                          <option value="high">

                            High

                          </option>

                        </select>

                      </div>


                    </div>


                    <div className="mb-3">

                      <label className="form-label">

                        Expected Result

                      </label>

                      <textarea
                        className="form-control"
                        rows="2"
                        value={
                          bugForm.expectedResult
                        }
                        onChange={(e) =>
                          setBugForm({
                            ...bugForm,

                            expectedResult:
                              e.target.value,

                          })
                        }
                      />

                    </div>


                    <div className="mb-3">

                      <label className="form-label">

                        Actual Result *

                      </label>

                      <textarea
                        required
                        className="form-control"
                        rows="3"
                        value={
                          bugForm.actualResult
                        }
                        onChange={(e) =>
                          setBugForm({
                            ...bugForm,

                            actualResult:
                              e.target.value,

                          })
                        }
                      />

                    </div>


                  </div>


                  <div className="modal-footer">

                    <button
                      type="button"
                      className="btn btn-light"
                      onClick={() =>
                        setShowBugForm(
                          false,
                        )
                      }
                    >

                      Cancel

                    </button>


                    <button
                      type="submit"
                      className="btn btn-danger"
                      disabled={
                        actionLoading
                      }
                    >

                      {
                        actionLoading
                          ? "Creating..."
                          : "Create Bug"
                      }

                    </button>

                  </div>


                </form>

              </div>

            </div>

          </div>

        )
      }

              {/* BUG DETAILS / ACTION MODAL */}
        {dialog.type && dialog.type !== "approveProject" && (
          <div
            className="modal-backdrop"
            onClick={() =>
              !actionLoading &&
              setDialog({
                type: null,
                data: null,
              })
            }
          >
            <div
              className="modal-container"
              onClick={(e) => e.stopPropagation()}
            >
              {dialog.type === "passTestCase" && (
                <>
                  <div className="modal-header">
                    <h3>Mark Test Case as Passed</h3>

                    <button
                      type="button"
                      className="modal-close"
                      disabled={actionLoading}
                      onClick={() =>
                        setDialog({
                          type: null,
                          data: null,
                        })
                      }
                    >
                      ×
                    </button>
                  </div>

                  <div className="modal-body">
                    <p className="modal-description">
                      Please enter the actual result observed during testing.
                    </p>

                    <label className="modal-label">
                      Actual Result
                    </label>

                    <textarea
                      className="modal-textarea"
                      rows={5}
                      value={dialogInput}
                      onChange={(e) =>
                        setDialogInput(e.target.value)
                      }
                      placeholder="Enter actual result..."
                      disabled={actionLoading}
                    />
                  </div>

                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      disabled={actionLoading}
                      onClick={() =>
                        setDialog({
                          type: null,
                          data: null,
                        })
                      }
                    >
                      Cancel
                    </button>

                    <button
                      type="button"
                      className="btn btn-success"
                      disabled={actionLoading}
                      onClick={confirmPassTestCase}
                    >
                      {actionLoading
                        ? "Saving..."
                        : "Mark as Passed"}
                    </button>
                  </div>
                </>
              )}

              {dialog.type === "blockTestCase" && (
                <>
                  <div className="modal-header">
                    <h3>Block Test Case</h3>

                    <button
                      type="button"
                      className="modal-close"
                      disabled={actionLoading}
                      onClick={() =>
                        setDialog({
                          type: null,
                          data: null,
                        })
                      }
                    >
                      ×
                    </button>
                  </div>

                  <div className="modal-body">
                    <p className="modal-description">
                      Please provide the reason why this test case
                      cannot be executed.
                    </p>

                    <label className="modal-label">
                      Blocking Reason
                    </label>

                    <textarea
                      className="modal-textarea"
                      rows={5}
                      value={dialogInput}
                      onChange={(e) =>
                        setDialogInput(e.target.value)
                      }
                      placeholder="Enter blocking reason..."
                      disabled={actionLoading}
                    />
                  </div>

                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      disabled={actionLoading}
                      onClick={() =>
                        setDialog({
                          type: null,
                          data: null,
                        })
                      }
                    >
                      Cancel
                    </button>

                    <button
                      type="button"
                      className="btn btn-warning"
                      disabled={actionLoading}
                      onClick={confirmBlockTestCase}
                    >
                      {actionLoading
                        ? "Saving..."
                        : "Block Test Case"}
                    </button>
                  </div>
                </>
              )}

              {dialog.type === "closeBug" && (
                <>
                  <div className="modal-header">
                    <h3>Close Bug</h3>

                    <button
                      type="button"
                      className="modal-close"
                      disabled={actionLoading}
                      onClick={() =>
                        setDialog({
                          type: null,
                          data: null,
                        })
                      }
                    >
                      ×
                    </button>
                  </div>

                  <div className="modal-body">
                    <div className="confirmation-icon success">
                      ✓
                    </div>

                    <p className="modal-description">
                      Have you verified that the developer's fix
                      completely resolves this bug?
                    </p>

                    {dialog.data?.title && (
                      <div className="confirmation-info">
                        <strong>Bug:</strong>{" "}
                        {dialog.data.title}
                      </div>
                    )}

                    <p className="modal-note">
                      Once closed, the linked test case will be
                      marked as passed.
                    </p>
                  </div>

                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      disabled={actionLoading}
                      onClick={() =>
                        setDialog({
                          type: null,
                          data: null,
                        })
                      }
                    >
                      Cancel
                    </button>

                    <button
                      type="button"
                      className="btn btn-success"
                      disabled={actionLoading}
                      onClick={confirmCloseBug}
                    >
                      {actionLoading
                        ? "Closing..."
                        : "Yes, Close Bug"}
                    </button>
                  </div>
                </>
              )}

              {dialog.type === "reopenBug" && (
                <>
                  <div className="modal-header">
                    <h3>Reopen Bug</h3>

                    <button
                      type="button"
                      className="modal-close"
                      disabled={actionLoading}
                      onClick={() =>
                        setDialog({
                          type: null,
                          data: null,
                        })
                      }
                    >
                      ×
                    </button>
                  </div>

                  <div className="modal-body">
                    <p className="modal-description">
                      The fix did not resolve the issue. Please
                      explain what happened during retesting.
                    </p>

                    {dialog.data?.title && (
                      <div className="confirmation-info">
                        <strong>Bug:</strong>{" "}
                        {dialog.data.title}
                      </div>
                    )}

                    <label className="modal-label">
                      Retest Failure Reason
                    </label>

                    <textarea
                      className="modal-textarea"
                      rows={5}
                      value={dialogInput}
                      onChange={(e) =>
                        setDialogInput(e.target.value)
                      }
                      placeholder="Describe why the bug is still present..."
                      disabled={actionLoading}
                    />
                  </div>

                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      disabled={actionLoading}
                      onClick={() =>
                        setDialog({
                          type: null,
                          data: null,
                        })
                      }
                    >
                      Cancel
                    </button>

                    <button
                      type="button"
                      className="btn btn-danger"
                      disabled={actionLoading}
                      onClick={confirmReopenBug}
                    >
                      {actionLoading
                        ? "Reopening..."
                        : "Reopen Bug"}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* APPROVE PROJECT MODAL */}
        {dialog.type === "approveProject" && (
          <div
            className="modal-backdrop"
            onClick={() =>
              !actionLoading &&
              setDialog({
                type: null,
                data: null,
              })
            }
          >
            <div
              className="modal-container"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <h3>Approve Project</h3>

                <button
                  type="button"
                  className="modal-close"
                  disabled={actionLoading}
                  onClick={() =>
                    setDialog({
                      type: null,
                      data: null,
                    })
                  }
                >
                  ×
                </button>
              </div>

              <div className="modal-body">
                <div className="confirmation-icon success">
                  ✓
                </div>

                <h4 className="confirmation-title">
                  Ready to approve?
                </h4>

                <p className="modal-description">
                  Are you sure you want to approve this project?
                </p>

                <p className="modal-note">
                  Please make sure all test cases have passed and
                  there are no active bugs remaining.
                </p>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  disabled={actionLoading}
                  onClick={() =>
                    setDialog({
                      type: null,
                      data: null,
                    })
                  }
                >
                  Cancel
                </button>

                <button
                  type="button"
                  className="btn btn-success"
                  disabled={actionLoading}
                  onClick={confirmApproveProject}
                >
                  {actionLoading
                    ? "Approving..."
                    : "Yes, Approve Project"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* BUG CREATION MODAL */}
        {showBugForm && (
          <div
            className="modal-backdrop"
            onClick={() => {
              if (!actionLoading) {
                setShowBugForm(false);
                setSelectedTestCase(null);
              }
            }}
          >
            <div
              className="modal-container bug-form-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <h3>Report Bug</h3>

                <button
                  type="button"
                  className="modal-close"
                  disabled={actionLoading}
                  onClick={() => {
                    setShowBugForm(false);
                    setSelectedTestCase(null);
                  }}
                >
                  ×
                </button>
              </div>

              <form onSubmit={handleCreateBug}>
                <div className="modal-body">
                  {selectedTestCase && (
                    <div className="selected-testcase">
                      <span className="selected-testcase-label">
                        Test Case
                      </span>

                      <strong>
                        {selectedTestCase.title}
                      </strong>
                    </div>
                  )}

                  <div className="form-group">
                    <label>
                      Bug Title
                      <span className="required">*</span>
                    </label>

                    <input
                      type="text"
                      value={bugForm.title}
                      onChange={(e) =>
                        setBugForm((prev) => ({
                          ...prev,
                          title: e.target.value,
                        }))
                      }
                      placeholder="Enter bug title"
                      disabled={actionLoading}
                    />
                  </div>

                  <div className="form-group">
                    <label>Description</label>

                    <textarea
                      rows={4}
                      value={bugForm.description}
                      onChange={(e) =>
                        setBugForm((prev) => ({
                          ...prev,
                          description: e.target.value,
                        }))
                      }
                      placeholder="Describe the issue..."
                      disabled={actionLoading}
                    />
                  </div>

                  <div className="form-group">
                    <label>
                      Steps to Reproduce
                    </label>

                    {bugForm.stepsToReproduce.map(
                      (step, index) => (
                        <div
                          className="step-input-row"
                          key={index}
                        >
                          <input
                            type="text"
                            value={step}
                            onChange={(e) => {
                              const steps = [
                                ...bugForm.stepsToReproduce,
                              ];

                              steps[index] = e.target.value;

                              setBugForm((prev) => ({
                                ...prev,
                                stepsToReproduce: steps,
                              }));
                            }}
                            placeholder={`Step ${index + 1}`}
                            disabled={actionLoading}
                          />

                          {bugForm.stepsToReproduce.length >
                            1 && (
                            <button
                              type="button"
                              className="remove-step-btn"
                              disabled={actionLoading}
                              onClick={() => {
                                const steps =
                                  bugForm.stepsToReproduce.filter(
                                    (_, i) => i !== index
                                  );

                                setBugForm((prev) => ({
                                  ...prev,
                                  stepsToReproduce:
                                    steps.length
                                      ? steps
                                      : [""],
                                }));
                              }}
                            >
                              ×
                            </button>
                          )}
                        </div>
                      )
                    )}

                    <button
                      type="button"
                      className="add-step-btn"
                      disabled={actionLoading}
                      onClick={() =>
                        setBugForm((prev) => ({
                          ...prev,
                          stepsToReproduce: [
                            ...prev.stepsToReproduce,
                            "",
                          ],
                        }))
                      }
                    >
                      + Add Step
                    </button>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Expected Result</label>

                      <textarea
                        rows={3}
                        value={bugForm.expectedResult}
                        onChange={(e) =>
                          setBugForm((prev) => ({
                            ...prev,
                            expectedResult:
                              e.target.value,
                          }))
                        }
                        placeholder="What should happen?"
                        disabled={actionLoading}
                      />
                    </div>

                    <div className="form-group">
                      <label>
                        Actual Result
                        <span className="required">*</span>
                      </label>

                      <textarea
                        rows={3}
                        value={bugForm.actualResult}
                        onChange={(e) =>
                          setBugForm((prev) => ({
                            ...prev,
                            actualResult:
                              e.target.value,
                          }))
                        }
                        placeholder="What actually happened?"
                        disabled={actionLoading}
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Severity</label>

                      <select
                        value={bugForm.severity}
                        onChange={(e) =>
                          setBugForm((prev) => ({
                            ...prev,
                            severity: e.target.value,
                          }))
                        }
                        disabled={actionLoading}
                      >
                        <option value="low">
                          Low
                        </option>
                        <option value="medium">
                          Medium
                        </option>
                        <option value="high">
                          High
                        </option>
                        <option value="critical">
                          Critical
                        </option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Priority</label>

                      <select
                        value={bugForm.priority}
                        onChange={(e) =>
                          setBugForm((prev) => ({
                            ...prev,
                            priority: e.target.value,
                          }))
                        }
                        disabled={actionLoading}
                      >
                        <option value="low">
                          Low
                        </option>
                        <option value="medium">
                          Medium
                        </option>
                        <option value="high">
                          High
                        </option>
                        <option value="urgent">
                          Urgent
                        </option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    disabled={actionLoading}
                    onClick={() => {
                      setShowBugForm(false);
                      setSelectedTestCase(null);
                    }}
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    className="btn btn-danger"
                    disabled={actionLoading}
                  >
                    {actionLoading
                      ? "Creating..."
                      : "Create Bug"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        <style>{`
          .modal-backdrop {
            position: fixed;
            inset: 0;
            background: rgba(15, 23, 42, 0.55);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 9999;
            padding: 20px;
          }

          .modal-container {
            width: 100%;
            max-width: 520px;
            max-height: 90vh;
            overflow-y: auto;
            background: #ffffff;
            border-radius: 14px;
            box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
          }

          .bug-form-modal {
            max-width: 760px;
          }

          .modal-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 20px 24px;
            border-bottom: 1px solid #e5e7eb;
          }

          .modal-header h3 {
            margin: 0;
            font-size: 20px;
            font-weight: 700;
            color: #111827;
          }

          .modal-close {
            border: 0;
            background: transparent;
            font-size: 28px;
            line-height: 1;
            color: #6b7280;
            cursor: pointer;
          }

          .modal-close:hover {
            color: #111827;
          }

          .modal-body {
            padding: 24px;
          }

          .modal-description {
            margin: 0 0 18px;
            color: #4b5563;
            line-height: 1.6;
          }

          .modal-note {
            margin: 16px 0 0;
            padding: 12px 14px;
            border-radius: 8px;
            background: #f3f4f6;
            color: #4b5563;
            font-size: 14px;
            line-height: 1.5;
          }

          .modal-label {
            display: block;
            margin-bottom: 8px;
            font-size: 14px;
            font-weight: 600;
            color: #374151;
          }

          .modal-textarea {
            width: 100%;
            min-height: 120px;
            resize: vertical;
            box-sizing: border-box;
            border: 1px solid #d1d5db;
            border-radius: 8px;
            padding: 12px;
            font-size: 14px;
            outline: none;
          }

          .modal-textarea:focus {
            border-color: #6366f1;
            box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
          }

          .modal-footer {
            display: flex;
            justify-content: flex-end;
            gap: 10px;
            padding: 16px 24px;
            border-top: 1px solid #e5e7eb;
          }

          .confirmation-icon {
            width: 52px;
            height: 52px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            margin: 0 auto 18px;
            font-size: 26px;
            font-weight: 700;
          }

          .confirmation-icon.success {
            background: #dcfce7;
            color: #16a34a;
          }

          .confirmation-title {
            margin: 0 0 8px;
            text-align: center;
            font-size: 18px;
            color: #111827;
          }

          .confirmation-info {
            padding: 12px 14px;
            border-radius: 8px;
            background: #f9fafb;
            color: #374151;
            font-size: 14px;
          }

          .selected-testcase {
            margin-bottom: 20px;
            padding: 14px;
            border-radius: 8px;
            background: #f3f4f6;
          }

          .selected-testcase-label {
            display: block;
            margin-bottom: 4px;
            font-size: 12px;
            color: #6b7280;
            text-transform: uppercase;
          }

          .form-group {
            margin-bottom: 18px;
          }

          .form-group label {
            display: block;
            margin-bottom: 7px;
            font-size: 14px;
            font-weight: 600;
            color: #374151;
          }

          .required {
            color: #dc2626;
            margin-left: 3px;
          }

          .form-group input,
          .form-group textarea,
          .form-group select {
            width: 100%;
            box-sizing: border-box;
            border: 1px solid #d1d5db;
            border-radius: 8px;
            padding: 10px 12px;
            font-size: 14px;
            outline: none;
          }

          .form-group input:focus,
          .form-group textarea:focus,
          .form-group select:focus {
            border-color: #6366f1;
            box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
          }

          .form-row {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 16px;
          }

          .step-input-row {
            display: flex;
            gap: 8px;
            margin-bottom: 8px;
          }

          .step-input-row input {
            flex: 1;
          }

          .remove-step-btn {
            width: 38px;
            border: 1px solid #fecaca;
            background: #fef2f2;
            color: #dc2626;
            border-radius: 8px;
            font-size: 20px;
            cursor: pointer;
          }

          .add-step-btn {
            border: 0;
            background: transparent;
            color: #4f46e5;
            font-weight: 600;
            cursor: pointer;
            padding: 5px 0;
          }

          .btn {
            border: 0;
            border-radius: 8px;
            padding: 10px 16px;
            font-size: 14px;
            font-weight: 600;
            cursor: pointer;
          }

          .btn:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }

          .btn-secondary {
            background: #e5e7eb;
            color: #374151;
          }

          .btn-success {
            background: #16a34a;
            color: #ffffff;
          }

          .btn-warning {
            background: #d97706;
            color: #ffffff;
          }

          .btn-danger {
            background: #dc2626;
            color: #ffffff;
          }

          @media (max-width: 700px) {
            .form-row {
              grid-template-columns: 1fr;
            }

            .modal-container {
              max-height: 95vh;
            }

            .modal-header,
            .modal-body,
            .modal-footer {
              padding-left: 16px;
              padding-right: 16px;
            }
          }
        `}</style>
      </div>
    );
  };

export default AssignedTestingDetails;